import usePersistedKeyValueStore from '../../../hooks/usePersistedKeyValueStore';

export type UseSearchTextArgs = {
    searchText: string;
    medicalProfileId: string;
};

export type UseSearchTextReturnValue = {
    historicalSearches: string[];
    onPersistCurrentSearch: () => void;
};

type PersistedSearch = {
    searchText: string;
    timestamp: number;
};

const scrubAndValidatePersistedSearch = (searches: PersistedSearch[]) => {
    // sort by timestamp
    const sortedSearches = [...searches].sort((a, b) => b.timestamp - a.timestamp);

    // remove by older than 7 days
    const now = Date.now();
    const sevenDaysAgo = now - 1000 * 60 * 60 * 24 * 7;
    const filteredSearches = sortedSearches.filter((search) => search.timestamp > sevenDaysAgo);

    // dedupe by searchText
    const dedupedSearches = filteredSearches.reduce((acc, search) => {
        if (!acc.find((s) => s.searchText === search.searchText)) {
            acc.push(search);
        }
        return acc;
    }, [] as PersistedSearch[]);

    // only keep the last 5 searches
    return dedupedSearches.slice(0, 5);
};

export default function useSearchHistory({
    searchText,
    medicalProfileId,
}: UseSearchTextArgs): UseSearchTextReturnValue {
    const persistedSearchStore = usePersistedKeyValueStore<PersistedSearch[]>({
        category: 'search-history',
        key: `medicalProfileId:${medicalProfileId}`,
        defaultValue: [],
        expiration: 1000 * 60 * 60 * 24 * 30, // 30 days
    });

    return {
        historicalSearches: scrubAndValidatePersistedSearch(persistedSearchStore.value).map(
            (search) => search.searchText,
        ),
        onPersistCurrentSearch: () => {
            const newSearches = scrubAndValidatePersistedSearch([
                ...persistedSearchStore.value,
                { searchText, timestamp: Date.now() },
            ]);
            persistedSearchStore.setValue(newSearches);
        },
    };
}
